// JOBE-0106: ReassignOwnerModal — Decision 4 ships v1.
//
// Lists candidate principals from the roster (humans + agents),
// commits via `PUT /api/entities/{projectURN}` with the new
// `assignedTo` URN, and on success triggers a detail refetch via
// the supplied `onCommitted` callback.
//
// Discuss-thread emission deferred to a follow-up per the spec
// amendment — the PUT path is the only write here.

import React, { useState, useMemo } from 'react';

function urnTail(urn) {
  if (!urn || typeof urn !== 'string') return '';
  return urn.split(':').pop() || urn;
}

function rosterAsList(roster) {
  const humans = Array.isArray(roster?.humans) ? roster.humans : [];
  const agents = Array.isArray(roster?.agents) ? roster.agents : [];
  // Roster entries are objects (per JOBE-0044.10.1 wire shape). Normalise
  // to {urn, label} for the picker; gracefully accept bare URN strings.
  const normalize = (entry, kindLabel) => {
    if (typeof entry === 'string') return { urn: entry, label: urnTail(entry), kind: kindLabel };
    const urn = entry?.urn || entry?.principal_urn || '';
    if (!urn) return null;
    return {
      urn,
      label: entry?.displayName || entry?.name || entry?.display_name || urnTail(urn),
      kind: kindLabel,
    };
  };
  return [
    ...humans.map((h) => normalize(h, 'human')).filter(Boolean),
    ...agents.map((a) => normalize(a, 'agent')).filter(Boolean),
  ];
}

export function ReassignOwnerModal({
  projectURN,
  currentOwnerURN,
  roster,
  onClose,
  onCommitted,
}) {
  const candidates = useMemo(() => rosterAsList(roster), [roster]);
  const [selected, setSelected] = useState(currentOwnerURN || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selected || selected === currentOwnerURN) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/entities/${encodeURIComponent(projectURN)}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          relations: { assignedTo: [selected] },
        }),
      });
      if (!res.ok) {
        setError({ status: res.status });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      if (onCommitted) onCommitted(selected);
      if (onClose) onClose();
    } catch (err) {
      setError({ status: 0, message: err?.message || String(err) });
      setSubmitting(false);
    }
  };

  return (
    <div
      className="reassign-owner-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Reassign owner"
      data-testid="reassign-owner-modal"
    >
      <form className="reassign-owner-modal-card card" onSubmit={onSubmit}>
        <header className="reassign-owner-modal-head">
          <h3 className="lane-title">Reassign owner</h3>
          <button
            type="button"
            className="reassign-owner-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="reassign-owner-modal-body">
          {candidates.length === 0 ? (
            <p className="muted">No principals available.</p>
          ) : (
            <ul className="reassign-owner-candidates">
              {candidates.map((c) => (
                <li key={c.urn} className="reassign-owner-candidate-row">
                  <label>
                    <input
                      type="radio"
                      name="reassign-owner"
                      value={c.urn}
                      checked={selected === c.urn}
                      onChange={() => setSelected(c.urn)}
                      data-testid={`reassign-radio-${c.urn}`}
                    />
                    <span className="reassign-owner-candidate-label">
                      <span className="mono">{c.label}</span>
                      <span className="muted mono"> · {c.kind}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {error ? (
            <p className="muted" data-testid="reassign-error">
              error{error.status ? ` (${error.status})` : ''}
            </p>
          ) : null}
        </div>
        <footer className="reassign-owner-modal-foot">
          <button
            type="button"
            className="reassign-owner-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="reassign-owner-commit"
            disabled={submitting || !selected || selected === currentOwnerURN}
            data-testid="reassign-owner-commit"
          >
            {submitting ? 'Saving…' : 'Reassign'}
          </button>
        </footer>
      </form>
    </div>
  );
}
